import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import { animalInfo, iljuImg, AnimalFace } from "./CupidAnimals";
import { useLang } from "../i18n/LanguageContext";
import { saveReport } from "../utils/savePdf";

const API_BASE = process.env.REACT_APP_API_URL || "";
// 상세 연애궁합 리포트는 무료(2026-08-04). 큐피드 수익은 방 참가비에서 나온다.

/* ── 타입 ── */
/* trait 는 옵셔널이다 — 2026-08-15 이전에 마감된 방에는 이 키가 없다.
   성격유형(네 글자)만 담는다. 애착유형은 방 화면에 내보내지 않는다. */
interface Trait { type4?: string; type4_now?: string; }
interface PersonOut { index: number; name: string; gender: string; day_stem?: string; day_branch?: string; trait?: Trait; }
interface Pair { a: number; b: number; score: number; grade: string; }
interface RoomResult { people: PersonOut[]; pairs: Pair[]; match_mode: string; }

/* ── 애니메이션 ── */
const floatC = keyframes`0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)}`;
const heartBeat = keyframes`0%,100%{transform:scale(1)} 30%{transform:scale(1.25)} 60%{transform:scale(0.95)}`;
const shimmerC = keyframes`0%{background-position:200% center} 100%{background-position:-200% center}`;
const pop = keyframes`0%{transform:scale(0);opacity:0} 60%{transform:scale(1.15)} 100%{transform:scale(1);opacity:1}`;
const fadeUp = keyframes`from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)}`;

/* ── styled ── */
/* 성격유형 네 글자 배지 — 이름 옆에 작게. 애착유형은 넣지 않는다(방은 여럿이 본다) */
const TypeBadge = styled.span`
  display:inline-block; margin-left:5px; padding:1px 6px; border-radius:999px;
  background:#2B2A26; color:#FFD24A; font-size:.68rem; font-weight:800;
  letter-spacing:.5px; vertical-align:middle;
`;
const GlobalC = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; }
  body {
    background-color:#FFD24A;
    background-image: radial-gradient(rgba(43,42,38,0.06) 1.6px, transparent 1.6px);
    background-size: 22px 22px;
  }
`;
const HeroChar = styled.img`
  width:150px; height:150px; object-fit:cover; object-position:center 30%; border-radius:50%;
  border:3px solid #2B2A26; margin:0 auto 8px; display:block;
  box-shadow: 0 5px 0 rgba(43,42,38,0.18);
  animation:${floatC} 3.6s ease-in-out infinite;
`;
const Wrap = styled.div`
  max-width: 560px; margin: 0 auto; padding: 0 0 60px;
  font-family: 'Pretendard',sans-serif; color:#2B2A26;
`;
const Hero = styled.div`
  text-align:center; padding: 22px 20px 12px; background:transparent; position:relative;
`;
const HeroTitle = styled.h1`
  margin: 4px 0 4px; font-size: 1.7rem; font-weight: 900; letter-spacing:-.03em; color:#2B2A26;
  text-shadow: 0 2px 0 #FFEBA6;
`;
const HeroSub = styled.p`margin:0; font-size:.88rem; color:#2B2A26; font-weight:700;`;
const BackLink = styled.button`
  display:inline-flex; align-items:center; gap:5px; margin:0 4px 8px;
  background:rgba(255,255,255,0.7); border:1.5px solid #2B2A26; border-radius:20px;
  padding:7px 14px; color:#2B2A26; font-size:13px; font-weight:700;
  cursor:pointer; font-family:inherit;
  &:hover{ background:#FBF6EA; }
`;
const Section = styled.div`padding: 20px 12px 0;`;
const RowLabel = styled.div`
  display:flex; align-items:center; justify-content:space-between; margin: 18px 6px 8px;
  font-size:1rem; font-weight:900; color:#2B2A26;
`;
const Toggle = styled.div`
  display:flex; gap:4px; justify-content:center; margin: 6px 0 14px;
  background:#fff; border:2px solid #2B2A26; border-radius:999px; padding:4px; width:fit-content; margin-left:auto; margin-right:auto;
  box-shadow: 0 2px 0 rgba(43,42,38,0.16);
`;
const ToggleBtn = styled.button<{ $on: boolean }>`
  padding:8px 16px; border:none; border-radius:999px; font-size:.82rem; font-weight:800; cursor:pointer;
  background:${p => p.$on ? "#D0654F" : "transparent"};
  color:${p => p.$on ? "#fff" : "#2B2A26"}; transition:.15s;
`;
const Arena = styled.div`
  background: #fff; border:2.5px solid #2B2A26;
  border-radius:22px; padding:8px 4px; margin:0 2px;
  box-shadow: 0 4px 0 rgba(43,42,38,0.18);
`;
const ColHead = styled.div`display:flex; justify-content:space-between; padding:2px 26px 0; font-size:.8rem; font-weight:800;`;
const MatchCard = styled.div<{ $mutual: boolean }>`
  display:flex; align-items:center; gap:10px; padding:12px 14px; margin:10px 4px; border-radius:18px;
  background:${p => p.$mutual ? "#FFF4CC" : "#fff"};
  border:2.5px solid #2B2A26;
  box-shadow: 0 3px 0 rgba(43,42,38,0.16);
  animation:${pop} .4s ease both;
`;
const RankNum = styled.div<{ $top: boolean }>`
  width:26px; flex-shrink:0; text-align:center; font-weight:900; font-size:${p => p.$top ? "1.15rem" : ".95rem"};
  color:${p => p.$top ? "#D0654F" : "#2B2A26"};
`;
const Avatar = styled.div<{ $male: boolean }>`
  width:38px; height:38px; border-radius:50%; flex-shrink:0; overflow:hidden;
  display:flex; align-items:center; justify-content:center;
  background:#fff;
`;
const ScorePill = styled.div<{ $c: string }>`
  margin-left:auto; padding:5px 13px; border:2px solid #2B2A26; border-radius:999px; font-size:.88rem; font-weight:900; color:#fff; background:${p => p.$c};
  box-shadow: 0 2px 0 rgba(43,42,38,0.2);
`;
const Card = styled.div`
  background:#fff; border:2.5px solid #2B2A26; border-radius:20px; padding:22px 18px; margin:14px 4px; text-align:center;
  box-shadow: 0 4px 0 rgba(43,42,38,0.18);
  animation:${fadeUp} .5s ease both;
`;
const BigHeart = styled.div`font-size:3rem; color:#C0392B; animation:${heartBeat} 1.1s ease-in-out infinite; margin-bottom:6px;`;
const BackBtn = styled.button`
  width:100%; padding:14px; margin:16px 4px 0; border:2.5px solid #2B2A26; border-radius:16px;
  background:#fff; color:#D0654F; font-weight:900; font-size:.95rem; cursor:pointer;
  box-shadow: 0 4px 0 rgba(43,42,38,0.2); transition:transform .1s, box-shadow .1s;
  &:hover{ background:#FFF9E8; } &:active{ transform:translateY(3px); box-shadow:0 1px 0 rgba(43,42,38,0.2); }
`;
const Muted = styled.div`text-align:center; color:#2B2A26; font-size:.9rem; padding:40px 20px; line-height:1.6; font-weight:600;`;
const ExpireNote = styled.div`text-align:center; color:#2B2A26; opacity:.75; font-size:.76rem; margin:16px 8px 0; line-height:1.5; font-weight:600;`;
const MeBanner = styled.div`
  display:flex; align-items:center; justify-content:center; gap:8px; flex-wrap:wrap;
  background:#FFF4CC; border:2.5px solid #2B2A26; border-radius:18px;
  box-shadow: 0 3px 0 rgba(43,42,38,0.16);
  padding:12px 14px; margin:0 4px 8px; font-weight:800; color:#2B2A26; font-size:.98rem;
`;
const HookCard = styled.div`
  background:#fff;
  border:2.5px solid #2B2A26; border-radius:20px; padding:20px 16px; margin:12px 4px 4px;
  text-align:center;
  box-shadow: 0 4px 0 rgba(43,42,38,0.18);
  animation:${fadeUp} .55s ease both;
  .he{ font-size:2.1rem; color:#C0392B; }
  .ht{ font-size:1.12rem; font-weight:900; color:#D0654F; margin:6px 0 6px; letter-spacing:-.02em; }
  .hs{ font-size:.9rem; color:#2B2A26; line-height:1.55; margin-bottom:6px; }
`;
const HookSelect = styled.select`
  width:100%; padding:12px; margin:14px 0 0; border:1.5px solid #2B2A26; border-radius:12px;
  font-size:.95rem; font-weight:700; color:#2B2A26; background:#fff; outline:none; box-sizing:border-box;
  cursor:pointer; &:focus{ border-color:#D0654F; }
`;
const HookInput = styled.input`
  width:100%; padding:12px; margin:10px 0 4px; border:1.5px solid #2B2A26; border-radius:12px;
  font-size:.92rem; color:#2B2A26; background:#fff; outline:none; box-sizing:border-box;
  &:focus{ border-color:#D0654F; }
`;
const HookBtn = styled.button`
  width:100%; padding:15px; margin-top:8px; border:2.5px solid #2B2A26; border-radius:16px;
  box-shadow: 0 4px 0 rgba(43,42,38,0.2);
  background:#D0654F; color:#fff; font-size:1rem; font-weight:900;
  cursor:pointer; transition:.15s;
  &:hover{ transform:translateY(-1px); } &:disabled{opacity:.55;cursor:not-allowed; transform:none;}
`;
const ReportBox = styled.div`
  background:#fff; border:2.5px solid #2B2A26; border-radius:20px; margin:10px 4px; overflow:hidden;
  box-shadow: 0 4px 0 rgba(43,42,38,0.18);
  .rt{ font-weight:900; color:#D0654F; text-align:center; padding:14px 12px 4px; font-size:1rem; }
`;

function genderEmoji(g: string) { return g === "male" ? "♂" : "♀"; }
const HEART_RED = "#C0392B";
const gColor = (g: string) => (g === "male" ? "#4A7BA8" : "#D0654F");
function GS({ g }: { g: string }) { return <span style={{ color: gColor(g), fontWeight: 900 }}>{genderEmoji(g)}</span>; }
function HRT() { return <span style={{ color: HEART_RED }}>♥</span>; }
function scoreColor(s: number) {
  if (s >= 70) return "#D0654F";   // 코랄 — 좋음
  if (s >= 50) return "#B8862A";   // 골드 — 보통
  return "#8C8677";                // 뮤트 — 노력 필요
}

/* 각자의 최고의 짝 + 상호(커플) 계산 */
function computeBest(res: RoomResult, includeSameSex: boolean) {
  const n = res.people.length;
  const score: number[][] = Array.from({ length: n }, () => Array(n).fill(-1));
  for (const p of res.pairs) { score[p.a][p.b] = p.score; score[p.b][p.a] = p.score; }
  const best: ({ j: number; score: number } | null)[] = [];
  for (let i = 0; i < n; i++) {
    let bj = -1, bs = -2;
    for (let j = 0; j < n; j++) {
      if (j === i || score[i][j] < 0) continue;
      if (!includeSameSex && res.people[j].gender === res.people[i].gender) continue;
      if (score[i][j] > bs) { bs = score[i][j]; bj = j; }
    }
    best.push(bj >= 0 ? { j: bj, score: bs } : null);
  }
  const mutual = new Set<string>();
  for (let i = 0; i < n; i++) {
    const b = best[i];
    if (b && best[b.j] && best[b.j]!.j === i) mutual.add([i, b.j].sort((a, c) => a - c).join("-"));
  }
  return { best, mutual, score };
}

/* 두 점 사이를 물결치듯(왔다갔다) 잇는 경로 — 양 끝은 노드에 딱 맞게 진폭 0 */
function wavyPath(s: { x: number; y: number }, t: { x: number; y: number }, amp: number, waves: number, bias = 0): string {
  const dx = t.x - s.x, dy = t.y - s.y;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len, py = dx / len;           // 진행방향 수직(perpendicular) 단위벡터
  const steps = Math.max(10, Math.round(len / 12));
  let d = `M${s.x.toFixed(1)},${s.y.toFixed(1)}`;
  for (let i = 1; i <= steps; i++) {
    const tt = i / steps;
    const taper = Math.sin(tt * Math.PI);        // 양 끝 0, 가운데 1
    const off = (Math.sin(tt * waves * Math.PI) * amp + bias) * taper;
    const x = s.x + dx * tt + px * off;
    const y = s.y + dy * tt + py * off;
    d += ` L${x.toFixed(1)},${y.toFixed(1)}`;
  }
  return d;
}

/* 일주 동물 타일 매칭 게임 — 누가 제일 잘 맞을까? (70점↑만 매칭) */
const MATCH_MIN = 70;
function TwoColumnArena({ res, meIndex }:
  { res: RoomResult; best?: any; mutual?: any; revealKey?: string; meIndex?: number | null }) {
  const { lang } = useLang();
  const L = (ko: string, en: string) => (lang === "en" ? en : ko);
  const males = res.people.filter(p => p.gender === "male");
  const females = res.people.filter(p => p.gender !== "male");
  const rows = Math.max(males.length, females.length, 1);
  const includeSame = res.match_mode === "all";

  const [focus, setFocus] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [tick, setTick] = useState(0);
  const [msg, setMsg] = useState("");

  const W = 500, NR = 27, rowH = 92, topPad = 46, botPad = 40;
  const H = topPad + (rows - 1) * rowH + NR + botPad + 20;
  const xMale = 92, xFemale = W - 92;

  const pos: Record<number, { x: number; y: number; male: boolean }> = {};
  males.forEach((p, k) => { pos[p.index] = { x: xMale, y: topPad + k * rowH, male: true }; });
  females.forEach((p, k) => { pos[p.index] = { x: xFemale, y: topPad + k * rowH, male: false }; });

  const scoreOf = (a: number, b: number) => {
    const pr = res.pairs.find(x => (x.a === a && x.b === b) || (x.a === b && x.b === a));
    return pr ? pr.score : -1;
  };
  // 특정 사람의 최고 상대(성별 규칙 반영)
  const bestOf = (idx: number) => {
    const me = res.people.find(p => p.index === idx)!;
    let bj = -1, bs = -1;
    res.people.forEach(o => {
      if (o.index === idx) return;
      if (!includeSame && o.gender === me.gender) return;
      const s = scoreOf(idx, o.index);
      if (s > bs) { bs = s; bj = o.index; }
    });
    return { j: bj, score: bs, name: me.name };
  };
  const shoot = (idx: number) => {
    const r = bestOf(idx);
    setFocus(idx); setShowAll(false); setTick(t => t + 1);
    if (r.j >= 0 && r.score >= MATCH_MIN) setMsg("");
    else if (r.j >= 0) setMsg(L(`${r.name}님은 아직 운명의 짝을 못 만났어요 (최고 ${r.score}점)`, `${r.name} hasn't met their destined match yet (best ${r.score} pts)`));
    else setMsg(L(`${r.name}님과 이어질 상대가 없어요`, `No one to match with ${r.name}`));
  };
  const showEveryone = () => { setShowAll(true); setFocus(null); setMsg(""); setTick(t => t + 1); };

  // 그릴 매칭 엣지 목록 (70점↑). 전체보기면 유니크 커플 전부, 아니면 focus 하나
  const uniqueEdges = () => {
    const seen = new Set<string>(); const arr: { from: number; to: number; score: number }[] = [];
    res.people.forEach(p => {
      const r = bestOf(p.index);
      if (r.j >= 0 && r.score >= MATCH_MIN) {
        const k = [p.index, r.j].sort((a, b) => a - b).join("-");
        if (!seen.has(k)) { seen.add(k); arr.push({ from: p.index, to: r.j, score: r.score }); }
      }
    });
    return arr;
  };
  let edgesToDraw: { from: number; to: number; score: number; delay: number }[] = [];
  if (showAll) edgesToDraw = uniqueEdges().map((e, i) => ({ ...e, delay: i * 0.22 }));
  else if (focus != null) {
    const r = bestOf(focus);
    if (r.j >= 0 && r.score >= MATCH_MIN) edgesToDraw = [{ from: focus, to: r.j, score: r.score, delay: 0 }];
  }
  const activeSet = new Set<number>();
  edgesToDraw.forEach(e => { activeSet.add(e.from); activeSet.add(e.to); });

  const shorten = (a: { x: number; y: number }, b: { x: number; y: number }, r: number) => {
    const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1;
    return { x: a.x + dx / d * r, y: a.y + dy / d * r };
  };

  return (
    <Arena>
      <div style={{ textAlign: "center", fontWeight: 900, color: "#2B2A26", fontSize: "1rem", padding: "8px 0 1px" }}>{L("누가 제일 잘 맞을까?", "Who's the best match?")}</div>
      <div style={{ textAlign: "center", fontSize: ".76rem", color: "#2B2A26", fontWeight: 600, paddingBottom: 4 }}>{L("동물 친구를 눌러 인연의 화살을 쏘아보세요 · 70점 이상만 매칭!", "Tap an animal to shoot the arrow of fate · matches only at 70 pts+!")}</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
        {/* 매칭 화살(일직선·화려) + 하트 뿅뿅 + 점수 — 클릭 1개 or 전체보기 여러개 */}
        {edgesToDraw.map((edge, ei) => {
          const A = pos[edge.from], B = pos[edge.to];
          if (!A || !B) return null;
          const s = shorten(A, B, NR + 4), t = shorten(B, A, NR + 4);
          const lineLen = Math.hypot(t.x - s.x, t.y - s.y) || 1;
          // 같은 열(동성 매칭)이면 짧은 세로선 대신 옆으로 크게 휜 곡선으로 — 항상 잘 보이게
          const sameCol = Math.abs(A.x - B.x) < 1;
          const bow = sameCol ? (A.x < W / 2 ? -80 : 80) : 0;
          const cx = (s.x + t.x) / 2 + bow, cy = (s.y + t.y) / 2;
          const straight = sameCol
            ? `M${s.x.toFixed(1)},${s.y.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${t.x.toFixed(1)},${t.y.toFixed(1)}`
            : `M${s.x.toFixed(1)},${s.y.toFixed(1)} L${t.x.toFixed(1)},${t.y.toFixed(1)}`;
          const approx = sameCol ? lineLen * 1.35 + Math.abs(bow) * 1.7 + 6 : lineLen + 6;
          const mx = sameCol ? 0.25 * s.x + 0.5 * cx + 0.25 * t.x : (s.x + t.x) / 2;
          const my = sameCol ? 0.25 * s.y + 0.5 * cy + 0.25 * t.y : (s.y + t.y) / 2;
          const gid = `ag${tick}-${ei}`;
          const d0 = edge.delay;
          const sparks = [0.26, 0.5, 0.74].map(f => {
            if (sameCol) { const u = 1 - f; return { x: u * u * s.x + 2 * u * f * cx + f * f * t.x, y: u * u * s.y + 2 * u * f * cy + f * f * t.y }; }
            return { x: s.x + (t.x - s.x) * f, y: s.y + (t.y - s.y) * f };
          });
          return (
            <g key={`edge-${tick}-${ei}`}>
              <defs>
                <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={s.x} y1={s.y} x2={t.x} y2={t.y}>
                  <stop offset="0%" stopColor="#F5B14C" /><stop offset="50%" stopColor="#E8618C" /><stop offset="100%" stopColor="#D0654F" />
                </linearGradient>
              </defs>
              <path d={straight} fill="none" stroke="#E8618C" strokeWidth={10} strokeLinecap="round" opacity="0.22"
                strokeDasharray={approx} strokeDashoffset={approx}>
                <animate attributeName="stroke-dashoffset" from={approx} to="0" begin={`${d0}s`} dur="0.5s" fill="freeze" />
              </path>
              <path d={straight} fill="none" stroke={`url(#${gid})`} strokeWidth={5} strokeLinecap="round"
                strokeDasharray={approx} strokeDashoffset={approx}>
                <animate attributeName="stroke-dashoffset" from={approx} to="0" begin={`${d0}s`} dur="0.5s" fill="freeze" />
              </path>
              {sparks.map((p, i) => (
                <path key={i} d={`M${p.x},${p.y - 5} L${p.x + 1.5},${p.y - 1.5} L${p.x + 5},${p.y} L${p.x + 1.5},${p.y + 1.5} L${p.x},${p.y + 5} L${p.x - 1.5},${p.y + 1.5} L${p.x - 5},${p.y} L${p.x - 1.5},${p.y - 1.5} Z`}
                  fill="#FFF3C4" stroke="#F5B14C" strokeWidth="0.6" opacity="0">
                  <animate attributeName="opacity" values="0;1;0" begin={`${(d0 + 0.35 + i * 0.14).toFixed(2)}s`} dur="1s" repeatCount="indefinite" />
                </path>
              ))}
              <text x={t.x} y={t.y - NR - 4} textAnchor="middle" dominantBaseline="central" fill="#C0392B" opacity="0" fontSize="20">♥
                <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.5;0.7;1" begin={`${d0}s`} dur="0.9s" fill="freeze" />
                <animate attributeName="font-size" values="6;6;30;22" keyTimes="0;0.5;0.72;1" begin={`${d0}s`} dur="0.9s" fill="freeze" />
              </text>
              {[0, 1, 2, 3, 4, 5].map(i => {
                const ang = (i / 6) * Math.PI * 2;
                return (
                  <text key={i} x={mx} y={my} fontSize={15} textAnchor="middle" dominantBaseline="central" fill="#C0392B" opacity="0">♥
                    <animateTransform attributeName="transform" type="translate" from="0 0" to={`${(Math.cos(ang) * 26).toFixed(0)} ${(Math.sin(ang) * 26 - 6).toFixed(0)}`} begin={`${d0 + 0.5}s`} dur="0.7s" fill="freeze" />
                    <animate attributeName="opacity" values="0;1;0" begin={`${d0 + 0.5}s`} dur="0.75s" fill="freeze" />
                    <animate attributeName="font-size" values="9;22;15" begin={`${d0 + 0.5}s`} dur="0.75s" fill="freeze" />
                  </text>
                );
              })}
              <g opacity="0"><animate attributeName="opacity" values="0;0;1" keyTimes="0;0.55;1" begin={`${d0}s`} dur="1s" fill="freeze" />
                <rect x={mx - 25} y={my - 14} width="50" height="28" rx="14" fill="#D0654F" stroke="#2B2A26" strokeWidth="2" />
                <text x={mx} y={my + 1} fontSize={13} fontWeight={900} textAnchor="middle" dominantBaseline="central" fill="#fff">{edge.score}{L("점", "")}</text>
              </g>
            </g>
          );
        })}

        {/* 사람 = 일주 동물 타일 */}
        {res.people.map(p => {
          const q = pos[p.index];
          if (!q) return null;
          const isMe = meIndex != null && p.index === meIndex;
          const isOn = focus === p.index || activeSet.has(p.index);
          return (
            <g key={p.index} onClick={() => shoot(p.index)} style={{ cursor: "pointer" }}>
              <circle cx={q.x} cy={q.y} r={NR + 14} fill="transparent" />
              {isOn && (
                <circle key={`h${tick}`} cx={q.x} cy={q.y} r={NR + 4} fill="none" stroke="#D0654F" strokeWidth={3}>
                  <animate attributeName="r" values={`${NR + 2};${NR + 10};${NR + 3}`} begin="0s" dur="0.6s" repeatCount="2" />
                  <animate attributeName="opacity" values="1;0.3;1" begin="0s" dur="0.6s" repeatCount="2" />
                </circle>
              )}
              {isMe && <circle cx={q.x} cy={q.y} r={NR + 8} fill="none" stroke="#eab308" strokeWidth={2.5} strokeDasharray="4 3" />}
              {(() => {
                const img = iljuImg(p.day_stem || "", p.day_branch || "");
                if (!img) return <circle cx={q.x} cy={q.y} r={NR} fill="#ECEAE4" />;
                return (
                  <>
                    <clipPath id={`clip${p.index}`}><circle cx={q.x} cy={q.y} r={NR} /></clipPath>
                    <image href={img} x={q.x - NR} y={q.y - NR} width={NR * 2} height={NR * 2}
                      clipPath={`url(#clip${p.index})`} preserveAspectRatio="xMidYMid slice" />
                  </>
                );
              })()}
              <text x={q.x} y={q.y + NR + 15} fontSize={12.5} fontWeight={800} textAnchor="middle" fill={isMe ? "#b8860b" : "#2B2A26"}>{p.name}{isMe ? L(" (나)", " (me)") : ""}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ textAlign: "center", fontSize: ".8rem", color: msg ? "#B8862A" : "#2B2A26", fontWeight: 700, padding: "2px 12px 6px", minHeight: 18 }}>
        {msg || (showAll ? L("전체 인연 결과예요 ♥", "Here are all the matches ♥") : L("동물 친구를 눌러보세요 ♥", "Tap an animal friend ♥"))}
      </div>
      <div style={{ textAlign: "center", paddingBottom: 10 }}>
        <button onClick={showEveryone}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#D0654F", color: "#fff", border: "2.5px solid #2B2A26", borderRadius: 999, padding: "9px 20px", fontSize: ".9rem", fontWeight: 900, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 3px 0 rgba(43,42,38,0.22)" }}>
          {L("♥ 최종 결과 한번에 보기", "♥ See all results at once")}
        </button>
      </div>
    </Arena>
  );
}

export default function CupidResultPage() {
  const { lang } = useLang();
  const L = (ko: string, en: string) => (lang === "en" ? en : ko);
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const code = (sp.get("code") || "").toUpperCase();
  const personToken = sp.get("person") || "";

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"open" | "closed" | "error">("open");
  const [title, setTitle] = useState("");
  const [roomFree, setRoomFree] = useState(false);
  const [waitCount, setWaitCount] = useState(0);
  const [result, setResult] = useState<RoomResult | null>(null);
  const [personData, setPersonData] = useState<any>(null);
  const [meIndex, setMeIndex] = useState<number | null>(null);
  const [includeSameSex, setIncludeSameSex] = useState(false);
  const [reveal, setReveal] = useState(0);
  const [errMsg, setErrMsg] = useState("");
  // 상세 연애궁합 업그레이드
  const [upEmail, setUpEmail] = useState("");
  const [upBusy, setUpBusy] = useState(false);
  const [upErr, setUpErr] = useState("");
  const [upHtml, setUpHtml] = useState("");
  const [upSent, setUpSent] = useState(false);
  const [targetIdx, setTargetIdx] = useState("");   // "" = 최고의 짝 자동, 그 외 = 선택한 상대 index
  // 실제로 생성된 리포트의 두 사람 이름(상단 배너·제목이 '최고의 짝'으로 잘못 보이던 문제 방지)
  const [upMe, setUpMe] = useState("");
  const [upPartner, setUpPartner] = useState("");

  async function doUpgrade(impUid?: string, merchantUid?: string) {
    setUpBusy(true); setUpErr("");
    try {
      const res = await axios.post(`${API_BASE}/api/v1/cupid/upgrade`, {
        person_token: personToken, email: upEmail.trim(), imp_uid: impUid, merchant_uid: merchantUid,
        target_index: targetIdx === "" ? undefined : Number(targetIdx),
      });
      setUpHtml(res.data.report_html || "");
      setUpSent(!!res.data.email_sent);
      setUpMe(res.data.me_name || "");
      setUpPartner(res.data.partner_name || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      setUpErr(e?.response?.data?.detail || L("리포트 생성에 실패했습니다. 잠시 후 다시 시도해주세요.", "Failed to generate the report. Please try again shortly."));
    } finally { setUpBusy(false); }
  }
  /* 상세 궁합 리포트는 무료가 됐다(2026-08-04) — 결제 단계 없이 바로 만든다. */
  function handleUpgrade() {
    setUpErr("");
    doUpgrade();
  }

  /* 휴대폰 결제 후 복귀 — 저장해 둔 선택값으로 리포트 생성을 이어서 실행 */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const impUid = q.get("imp_uid") || "";
    const success = q.get("imp_success") ?? q.get("success");
    if (!impUid && !success) return;
    let saved: any = null;
    try { saved = JSON.parse(sessionStorage.getItem("cupidPending") || "null"); } catch { /* noop */ }
    sessionStorage.removeItem("cupidPending");
    // 주소창에서 결제 파라미터만 정리 (person 토큰은 유지)
    const keep = q.get("person") || saved?.personToken || "";
    window.history.replaceState({}, "", keep ? `/cupid_r?person=${encodeURIComponent(keep)}` : "/cupid_r");
    if (success === "false" || success === "0") {
      setUpErr(q.get("error_msg") || L("결제가 취소되었습니다.", "Payment was cancelled."));
      return;
    }
    if (!saved) return;
    if (saved.email) setUpEmail(saved.email);
    if (saved.targetIdx !== undefined) setTargetIdx(saved.targetIdx);
    setUpBusy(true);
    (async () => {
      try {
        const res = await axios.post(`${API_BASE}/api/v1/cupid/upgrade`, {
          person_token: saved.personToken, email: (saved.email || "").trim(),
          imp_uid: impUid, merchant_uid: q.get("merchant_uid") || saved.merchantUid,
          target_index: saved.targetIdx === "" || saved.targetIdx === undefined ? undefined : Number(saved.targetIdx),
        });
        setUpHtml(res.data.report_html || "");
        setUpSent(!!res.data.email_sent);
        setUpMe(res.data.me_name || "");
        setUpPartner(res.data.partner_name || "");
      } catch (e: any) {
        setUpErr(e?.response?.data?.detail || L("리포트 생성에 실패했습니다. 잠시 후 다시 시도해주세요.", "Failed to generate the report. Please try again shortly."));
      } finally { setUpBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        if (personToken) {
          const res = await axios.get(`${API_BASE}/api/v1/cupid/result/person/${personToken}`);
          if (!alive) return;
          setTitle(res.data.title || ""); setRoomFree(!!res.data.free);
          if (res.data.status === "closed") {
            setStatus("closed"); setPersonData(res.data);
            if (res.data.result) {                       // 전체 화살표 화면 + 본인 강조
              const rr = res.data.result as RoomResult;
              setResult(rr); setMeIndex(typeof res.data.my_index === "number" ? res.data.my_index : null);
              setIncludeSameSex(rr.match_mode === "all"); setReveal(v => v + 1);
            }
          } else { setStatus("open"); setWaitCount(res.data.count || 0); }
        } else if (code) {
          const res = await axios.get(`${API_BASE}/api/v1/cupid/room/${code}/result`);
          if (!alive) return;
          setTitle(res.data.title || ""); setRoomFree(!!res.data.free);
          if (res.data.status === "closed") {
            setStatus("closed");
            const r = res.data.result as RoomResult;
            setResult(r);
            setIncludeSameSex(r.match_mode === "all");
            setReveal(v => v + 1);
          } else { setStatus("open"); setWaitCount(res.data.count || 0); }
        } else {
          setStatus("error"); setErrMsg(L("결과 주소가 올바르지 않습니다.", "The result link is invalid."));
        }
      } catch (e: any) {
        if (!alive) return;
        setStatus("error");
        setErrMsg(e?.response?.data?.detail || L("결과를 불러오지 못했습니다.", "Couldn't load the results."));
      } finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [code, personToken]);

  const { best, mutual } = useMemo(
    () => result ? computeBest(result, includeSameSex) : { best: [], mutual: new Set<string>() },
    [result, includeSameSex]);

  const homePath = roomFree ? "/cupid_new" : "/cupid";

  if (loading) return (<Wrap><GlobalC /><Muted>{L("불러오는 중…", "Loading…")}</Muted></Wrap>);

  /* ── 아직 마감 전 ── */
  if (status === "open") {
    return (
      <Wrap>
        <GlobalC />
        <Hero><HeroChar src="/woo_02.webp" alt="우연" /><HeroTitle>{title || L("우연의 화살", "Wooyeon's Arrow")}</HeroTitle>
          <HeroSub>{L("아직 주최자가 마감하지 않았어요", "The host hasn't closed the room yet")}</HeroSub></Hero>
        <div style={{ padding: "14px 16px 0" }}><BackLink onClick={() => navigate(homePath)}>{L("← 이전", "← Back")}</BackLink></div>
        <Muted>
          {lang === "en" ? <><b>{waitCount}</b> people have joined so far.</> : <>현재 <b>{waitCount}명</b>이 참가했어요.</>}<br />
          {L("주최자가 마감하면 이 화면에서 결과가 나타납니다.", "Once the host closes the room, results appear here.")}<br />
          {L("이 링크를 저장해두고 나중에 다시 열어보세요! ✉", "Save this link and reopen it later! ✉")}
        </Muted>
        <BackBtn onClick={() => window.location.reload()}>{L("새로고침", "Refresh")}</BackBtn>
        <SiteFooter />
      </Wrap>
    );
  }

  if (status === "error") {
    return (<Wrap><GlobalC /><Hero><HeroChar src="/woo_02.webp" alt="우연" /><HeroTitle>{L("결과를 찾을 수 없어요", "Result not found")}</HeroTitle></Hero>
      <div style={{ padding: "14px 16px 0" }}><BackLink onClick={() => navigate(homePath)}>{L("← 이전", "← Back")}</BackLink></div>
      <Muted>{errMsg}</Muted><SiteFooter /></Wrap>);
  }

  /* ── 개인 결과 (전체 결과 데이터가 없을 때만 카드 폴백) ── */
  if (personToken && personData && !result) {
    const me = personData.me;
    const b = personData.best;
    return (
      <Wrap>
        <GlobalC />
        <Hero><HeroChar src="/woo_02.webp" alt="우연" /><HeroTitle>{L(`${me?.name}님의 최고의 짝`, `${me?.name}'s best match`)}</HeroTitle>
          <HeroSub>{title}</HeroSub></Hero>
        <Section>
          <BackLink onClick={() => navigate(homePath)}>{L("← 이전", "← Back")}</BackLink>
          {b ? (
            <Card>
              <BigHeart>{b.mutual ? "♥" : "♥"}</BigHeart>
              <div style={{ fontSize: "1.15rem", fontWeight: 900, color: "#2B2A26" }}>
                <GS g={me?.gender || ""} /> {me?.name} &nbsp;→&nbsp; <GS g={b.gender} /> {b.name}
              </div>
              <div style={{ margin: "12px 0" }}>
                <ScorePill $c={scoreColor(b.score)} style={{ display: "inline-block", fontSize: "1.05rem", padding: "8px 20px" }}>
                  {b.score}{L("점", " pts")} · {b.grade}
                </ScorePill>
              </div>
              {b.mutual
                ? <div style={{ color: "#B8862A", fontWeight: 900, fontSize: "1.05rem" }}><HRT /> {L("서로가 최고의 짝! 커플 성사!", "You're each other's best match! It's a couple!")}</div>
                : <div style={{ color: "#2B2A26", fontSize: ".9rem" }}>{L("당신이 가장 잘 맞는 상대예요.", "You're their best match.")}</div>}
            </Card>
          ) : (
            <Card><BigHeart>♡</BigHeart><div style={{ color: "#2B2A26" }}>{L("아쉽게도 이번엔 짝을 찾지 못했어요.", "Sadly, no match was found this time.")}</div></Card>
          )}
          <ExpireNote>{L("이 결과는 방 생성 후 12시간이 지나면 자동으로 삭제됩니다.", "This result is automatically deleted 12 hours after the room was created.")}</ExpireNote>
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  /* ── 전체 결과 (방 전체 / 개인 링크 공용) ── */
  if (result) {
    const revealKey = `${includeSameSex}-${reveal}-${meIndex}`;
    const allowToggle = result.match_mode === "all"; // 동성포함 방만 토글 노출
    const meBest = personData?.best;                  // 개인 링크일 때 본인 짝
    const meName = personData?.me?.name;
    return (
      <Wrap>
        <GlobalC />
        <Hero><HeroChar src="/woo_02.webp" alt="우연" />
          <HeroTitle>{meName ? L(`${meName}님의 짝 찾기`, `${meName}'s match`) : L("우연의 화살 결과", "Wooyeon's Arrow result")}</HeroTitle>
          <HeroSub>{meName ? L("나와 가장 잘 맞는 순위를 확인하세요", "See who matches you best") : (title ? L(`${title} · 화살이 이어준 짝`, `${title} · matches linked by the arrow`) : L("화살이 이어준 짝을 확인하세요", "See the matches linked by the arrow"))}</HeroSub></Hero>
        <Section>
          <BackLink onClick={() => navigate(homePath)}>{L("← 이전", "← Back")}</BackLink>
          {/* 상단 '나의 최고의 짝' 배너 — 특정 상대 리포트를 보는 중엔 숨긴다(그 상대와 혼동 방지) */}
          {personData && !upHtml && (
            meBest
              ? <MeBanner><HRT /> {L("나의 최고의 짝", "My best match")}: <GS g={meBest.gender} /> {meBest.name} · {meBest.score}{L("점", " pts")}
                  {meBest.mutual && <span style={{ color: "#B8862A" }}>· {L("커플 성사!", "It's a couple!")}</span>}
                  {/* 성격유형 — 옛 방에는 trait 키가 없어 있을 때만 그린다 */}
                  {(personData?.me?.trait?.type4 || meBest.trait?.type4) && (
                    <div style={{ marginTop: 6, fontSize: ".82rem", fontWeight: 700 }}>
                      {personData?.me?.trait?.type4 && <>{meName} <TypeBadge>{personData.me.trait.type4}</TypeBadge></>}
                      {personData?.me?.trait?.type4 && meBest.trait?.type4 && <span style={{ margin: "0 6px" }}>♥</span>}
                      {meBest.trait?.type4 && <>{meBest.name} <TypeBadge>{meBest.trait.type4}</TypeBadge></>}
                    </div>
                  )}</MeBanner>
              : <MeBanner>{L("아쉽게도 이번엔 짝을 찾지 못했어요 ♡", "Sadly, no match was found this time ♡")}</MeBanner>
          )}

          {/* 상세 연애궁합 업그레이드 (개인 링크 · 짝이 있을 때만) */}
          {personData && meBest && (
            upHtml ? (
              <ReportBox>
                <div className="rt">✉ {upMe || meName} ♥ {upPartner || meBest.name} · {L("연애궁합 리포트", "Love compatibility report")}</div>
                {upSent && <div style={{ textAlign: "center", color: "#2B2A26", fontSize: ".82rem", paddingBottom: 6 }}>{L("입력하신 메일로도 보내드렸어요 ✉", "We also sent it to your email ✉")}</div>}
                {/* 리포트는 재발급 안 됨 — 저장 안내 */}
                <div style={{ margin: "0 12px 12px", padding: "13px 15px", background: "#FFF3BF", border: "2px solid #E8590C", borderRadius: 12, color: "#7C2D12" }}>
                  <div style={{ fontSize: ".9rem", fontWeight: 900, marginBottom: 4 }}>
                    ⚠️ {L("결과 리포트는 재발급되지 않아요", "Your report cannot be re-issued")}
                  </div>
                  <div style={{ fontSize: ".78rem", lineHeight: 1.6 }}>
                    {L("이 페이지를 벗어나면 다시 열 수 없어요. 지금 바로 저장해 두세요.",
                       "Once you leave this page you can't open it again. Save it now.")}
                  </div>
                  <button onClick={() => saveReport(upHtml, "우연_연애궁합_리포트")}
                    style={{ marginTop: 10, width: "100%", background: "#E8590C", color: "#fff", border: "none", borderRadius: 10, padding: "11px 16px", fontSize: ".85rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                    {L("📄 지금 PDF로 저장하기", "📄 Save as PDF now")}
                  </button>
                </div>
                <iframe title="궁합 리포트" srcDoc={upHtml} style={{ width: "100%", border: "none", minHeight: 600 }}
                  onLoad={e => { try { const f = e.currentTarget; f.style.height = ((f.contentWindow?.document.body.scrollHeight || 600) + 30) + "px"; } catch { /* noop */ } }} />
                <button onClick={() => { setUpHtml(""); setUpErr(""); }}
                  style={{ display: "block", width: "calc(100% - 24px)", margin: "10px 12px 14px", padding: 12, border: "1.5px solid #2B2A26", borderRadius: 12, background: "#fff", color: "#D0654F", fontWeight: 800, cursor: "pointer" }}>
                  {L("← 다른 상대와 궁합 보기", "← Check compatibility with someone else")}
                </button>
              </ReportBox>
            ) : (
              (() => {
                const others = result.people.filter(p => p.index !== meIndex);
                const selName = targetIdx === ""
                  ? meBest.name
                  : (others.find(p => String(p.index) === targetIdx)?.name || meBest.name);
                return (
                  <HookCard>
                    <div className="he">♥</div>
                    <div className="ht">{L("이 사람과 나… 사주로 보면 얼마나 잘 맞을까?", "This person and me… how well do we match by saju?")}</div>
                    <div className="hs">
                      {lang === "en"
                        ? <>Personality fit, inner feelings, the art of the chase, even the <b>temperature of the spark</b>—</>
                        : <>성격 궁합, 속마음, 밀당의 기술, <b>설렘의 온도</b>까지—</>}<br />
                      {lang === "en"
                        ? <>a full love-compatibility report on you and <b>{selName}</b>, revealed in detail.</>
                        : <><b>{selName}</b>님과 나, 정식 연애궁합 리포트로 낱낱이 풀어드려요.</>}
                    </div>
                    <HookSelect value={targetIdx} onChange={e => setTargetIdx(e.target.value)}>
                      <option value="">{L(`♥ 최고의 짝 — ${meBest.name}`, `♥ Best match — ${meBest.name}`)}</option>
                      {others.map(p => (
                        <option key={p.index} value={p.index}>{genderEmoji(p.gender)} {p.name}</option>
                      ))}
                    </HookSelect>
                    <HookInput type="email" placeholder={L("결과 받을 이메일 (선택)", "Email to receive the result (optional)")}
                      value={upEmail} onChange={e => setUpEmail(e.target.value)} />
                    {/* 리포트는 재발급 안 됨 — 결제 전 안내 */}
                    <div style={{ margin: "10px 0 4px", padding: "11px 13px", background: "#FFF3BF", border: "2px solid #E8590C", borderRadius: 12, color: "#7C2D12", textAlign: "left" }}>
                      <div style={{ fontSize: ".85rem", fontWeight: 900, marginBottom: 3 }}>
                        ⚠️ {L("결과 리포트는 재발급되지 않아요", "Your report cannot be re-issued")}
                      </div>
                      <div style={{ fontSize: ".75rem", lineHeight: 1.55 }}>
                        {L("결과가 나오면 바로 PDF로 저장해 주세요. 이메일을 입력하시면 메일로도 보내드려요.",
                           "Save it as a PDF as soon as it appears. Enter your email and we'll send you a copy too.")}
                      </div>
                    </div>
                    <HookBtn onClick={handleUpgrade} disabled={upBusy}>
                      {upBusy ? L("리포트 준비 중…", "Preparing report…") : L(`✉ ${selName}님과의 궁합 자세히 보기 (무료)`, `✉ See detailed compatibility with ${selName} (free)`)}
                    </HookBtn>
                    {upErr && <div style={{ color: "#d33", fontSize: ".82rem", marginTop: 8, fontWeight: 600 }}>{upErr}</div>}
                  </HookCard>
                );
              })()
            )
          )}
          {personData ? (
            /* 개인 페이지 — 나와 잘 맞는 순위 (1위 → 꼴찌) */
            (() => {
              const meG = personData.me?.gender;
              const rows = result.people
                .filter(p => p.index !== meIndex && (includeSameSex || p.gender !== meG))
                .map(p => {
                  const pr = result.pairs.find(x => (x.a === meIndex && x.b === p.index) || (x.a === p.index && x.b === meIndex));
                  return { p, score: pr ? pr.score : 0, grade: pr ? pr.grade : "" };
                })
                .sort((a, b) => b.score - a.score);
              return (
                <>
                  <RowLabel><span>{L("나와 잘 맞는 순위", "Ranked by match with me")}</span>
                    <span style={{ fontSize: ".76rem", color: "#2B2A26", fontWeight: 600 }}>{L(`총 ${rows.length}명`, `${rows.length} total`)}</span></RowLabel>
                  {rows.length === 0 && <Muted>{L("비교할 상대가 없어요.", "No one to compare with.")}</Muted>}
                  {rows.map((r, i) => {
                    const isTop = i === 0;
                    const isMutualBest = isTop && !!meBest && !!meBest.mutual && meBest.name === r.p.name;
                    return (
                      <MatchCard key={r.p.index} $mutual={isTop}>
                        <RankNum $top={isTop}>{i + 1}</RankNum>
                        <Avatar $male={r.p.gender === "male"}><AnimalFace stem={r.p.day_stem || ""} branch={r.p.day_branch || ""} size={34} /></Avatar>
                        <div style={{ lineHeight: 1.35 }}>
                          <div style={{ fontWeight: 800, fontSize: ".95rem" }}>
                            {r.p.name}
                            {r.p.trait?.type4 && <TypeBadge>{r.p.trait.type4}</TypeBadge>}
                            {isMutualBest && <span><HRT /><span style={{ color: "#B8862A" }}> {L("커플 성사!", "It's a couple!")}</span></span>}
                          </div>
                          <div style={{ fontSize: ".82rem", color: "#2B2A26" }}>{r.grade}</div>
                        </div>
                        <ScorePill $c={scoreColor(r.score)}>{r.score}{L("점", "")}</ScorePill>
                      </MatchCard>
                    );
                  })}
                </>
              );
            })()
          ) : (
            /* 방장 페이지 — 전체 아레나 + 각자의 최고의 짝 (기존 그대로) */
            <>
              {allowToggle && (
                <Toggle>
                  <ToggleBtn $on={!includeSameSex} onClick={() => { setIncludeSameSex(false); setReveal(v => v + 1); }}>{L("이성 궁합만", "Opposite-sex only")}</ToggleBtn>
                  <ToggleBtn $on={includeSameSex} onClick={() => { setIncludeSameSex(true); setReveal(v => v + 1); }}>{L("동성 포함", "Same-sex included")}</ToggleBtn>
                </Toggle>
              )}

              <TwoColumnArena res={result} best={best} mutual={mutual} revealKey={revealKey} meIndex={meIndex} />

              <RowLabel><span>{L("각자의 최고의 짝", "Everyone's best match")}</span></RowLabel>
              {result.people
                .map((p, i) => ({ p, i, b: best[i] }))
                .sort((a, c) => (c.b?.score ?? -1) - (a.b?.score ?? -1))
                .map(({ p, i, b }) => {
                const key = b ? [i, b.j].sort((a, c) => a - c).join("-") : "";
                const matched = !!b && b.score >= MATCH_MIN;
                const isMutual = matched && mutual.has(key);
                const partner = b ? result.people[b.j] : null;
                return (
                  <MatchCard key={i} $mutual={isMutual} style={{ justifyContent: "space-between", gap: 4 }}>
                    {/* 왼쪽 — 본인 */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "33%" }}>
                      <Avatar $male={p.gender === "male"}><AnimalFace stem={p.day_stem || ""} branch={p.day_branch || ""} size={38} /></Avatar>
                      <div style={{ fontSize: ".8rem", fontWeight: 800, marginTop: 3, textAlign: "center", lineHeight: 1.2 }}>{p.name}</div>
                    </div>
                    {/* 가운데 — 하트 + 점수 */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 58 }}>
                      <div style={{ fontSize: "1.35rem", color: matched ? "#C0392B" : "#CFC8BB", lineHeight: 1 }}>{matched ? "♥" : "♡"}</div>
                      {matched
                        ? <ScorePill $c={scoreColor(b!.score)} style={{ margin: "3px 0 0" }}>{b!.score}{L("점", "")}</ScorePill>
                        : <span style={{ fontSize: ".68rem", color: "#2B2A26", fontWeight: 700, marginTop: 2 }}>{b ? `${b.score}${L("점", "")}` : "-"}</span>}
                      {isMutual && <div style={{ fontSize: ".62rem", color: "#B8862A", fontWeight: 800, marginTop: 2 }}>{L("커플 성사!", "Couple!")}</div>}
                    </div>
                    {/* 오른쪽 — 짝 */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "33%" }}>
                      {matched && partner ? (
                        <>
                          <Avatar $male={partner.gender === "male"}><AnimalFace stem={partner.day_stem || ""} branch={partner.day_branch || ""} size={38} /></Avatar>
                          <div style={{ fontSize: ".8rem", fontWeight: 800, marginTop: 3, textAlign: "center", lineHeight: 1.2 }}>{partner.name}</div>
                        </>
                      ) : (
                        <div style={{ fontSize: ".76rem", color: "#2B2A26", fontWeight: 700, textAlign: "center", lineHeight: 1.3 }}>{lang === "en" ? <>No match<br />yet</> : <>아직 짝이<br />없어요</>}</div>
                      )}
                    </div>
                  </MatchCard>
                );
              })}
            </>
          )}
          <ExpireNote>{L("이 결과는 방 생성 후 12시간이 지나면 자동으로 삭제됩니다.", "This result is automatically deleted 12 hours after the room was created.")}</ExpireNote>
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  return (<Wrap><GlobalC /><Muted>{L("결과가 없습니다.", "No results.")}</Muted></Wrap>);
}
